import { ethers } from 'ethers';

export interface WorkflowStep {
    id: string;
    title: string;
    description: string;
    type: 'function_builder' | 'timelock_schedule' | 'multisig_execute' | 'schedule_selector' | 'timelock_execute';
    inputMappings: { [paramName: string]: string | { type: 'input' | 'output'; source?: string; default?: string } };
    outputMappings: { [outputName: string]: string };
    estimatedTime: number; // in seconds
    dependencies?: string[]; // step IDs this step depends on
}

export interface WorkflowTemplate {
    id: string;
    name: string;
    description: string;
    category: 'governance' | 'defi' | 'security' | 'utility';
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    estimatedTime: number; // in seconds
    steps: WorkflowStep[];
    tags: string[];
    version: string;
    prerequisites?: string[]; // Optional prerequisites
    securityNotes?: string[]; // Optional security notes
}

// Validation functions
export const validateWorkflowStep = (
    step: WorkflowStep,
    previousOutputs: { [stepId: string]: any }
): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Check dependencies
    if (step.dependencies) {
        for (const depId of step.dependencies) {
            if (!previousOutputs[depId]) {
                errors.push(`Missing dependency: ${depId}`);
            }
        }
    }

    // Check input mappings
    for (const [paramName, mapping] of Object.entries(step.inputMappings)) {
        if (typeof mapping === 'object' && mapping.type === 'output') {
            const sourceStep = mapping.source;
            if (sourceStep && !previousOutputs[sourceStep]) {
                errors.push(`Missing output from step: ${sourceStep} for parameter: ${paramName}`);
            }
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

export const resolveWorkflowInputs = (
    step: WorkflowStep,
    previousOutputs: { [stepId: string]: any },
    userInputs: { [paramName: string]: string }
): { [paramName: string]: string } => {
    const resolvedInputs: { [paramName: string]: string } = {};

    for (const [paramName, mapping] of Object.entries(step.inputMappings)) {
        if (typeof mapping === 'string') {
            // Direct value
            resolvedInputs[paramName] = mapping;
        } else if (mapping.type === 'input') {
            // User input
            resolvedInputs[paramName] = userInputs[paramName] || mapping.default || '';
        } else if (mapping.type === 'output') {
            // Output from previous step
            const sourceStep = mapping.source;
            if (sourceStep && previousOutputs[sourceStep]) {
                const outputKey = paramName; // or mapping.outputKey if different
                resolvedInputs[paramName] = previousOutputs[sourceStep][outputKey] || mapping.default || '';
            }
        }
    }

    return resolvedInputs;
};

export const calculateWorkflowProgress = (
    completedSteps: string[],
    totalSteps: number
): number => {
    return Math.round((completedSteps.length / totalSteps) * 100);
};

export const estimateRemainingTime = (
    template: WorkflowTemplate,
    completedSteps: string[]
): number => {
    const remainingSteps = template.steps.filter(step => !completedSteps.includes(step.id));
    return remainingSteps.reduce((total, step) => total + step.estimatedTime, 0);
};

// Workflow Templates
export const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
    {
        id: 'memer_function_to_multisig',
        name: 'Memer Function → Timelock → MultiSig',
        description: 'Execute a Memer function through timelock scheduling and MultiSig execution',
        category: 'governance',
        difficulty: 'intermediate',
        estimatedTime: 15, // 15 minutes
        tags: ['memer', 'timelock', 'multisig', 'governance'],
        version: '1.0.0',
        prerequisites: [
            'Wallet connected with sufficient ETH for gas fees',
            'Access to MultiSig wallet with required signatures',
            'Timelock controller deployed and configured'
        ],
        securityNotes: [
            'Verify function parameters before scheduling',
            'Ensure timelock delay is appropriate for the operation',
            'Confirm MultiSig signers are available for execution'
        ],
        steps: [
            {
                id: 'step1_function_builder',
                title: 'Build Memer Function',
                description: 'Select and configure the Memer function to execute',
                type: 'function_builder',
                inputMappings: {
                    // User will select contract and function manually
                },
                outputMappings: {
                    functionData: 'encoded_function_data',
                    targetContract: 'target_contract_address',
                    functionName: 'selected_function_name'
                },
                estimatedTime: 300 // 5 minutes
            },
            {
                id: 'step2_timelock_schedule',
                title: 'Schedule in Timelock',
                description: 'Schedule the function execution in the timelock controller',
                type: 'timelock_schedule',
                inputMappings: {
                    target: { type: 'output', source: 'step1_function_builder' },
                    value: '0',
                    data: { type: 'output', source: 'step1_function_builder' },
                    predecessor: '0x0000000000000000000000000000000000000000000000000000000000000000',
                    salt: { type: 'input' },
                    delay: { type: 'input' }
                },
                outputMappings: {
                    scheduledTxHash: 'transaction_hash',
                    executionTime: 'execution_timestamp',
                    salt: 'generated_salt'
                },
                estimatedTime: 300, // 5 minutes
                dependencies: ['step1_function_builder']
            },
            {
                id: 'step3a_multisig_selection',
                title: 'Select MultiSig & Function',
                description: 'Choose the MultiSig wallet and execution function',
                type: 'function_builder',
                inputMappings: {
                    // User will select MultiSig contract and function
                },
                outputMappings: {
                    multiSigAddress: 'selected_multisig_address',
                    functionName: 'selected_function_name',
                    contractName: 'selected_contract_name'
                },
                estimatedTime: 120, // 2 minutes
                dependencies: ['step2_timelock_schedule']
            },
            {
                id: 'step3b_multisig_build',
                title: 'Build MultiSig Transaction',
                description: 'Build the MultiSig transaction with pre-filled parameters',
                type: 'function_builder',
                inputMappings: {
                    to: { type: 'output', source: 'step2_timelock_schedule' },
                    value: '0',
                    data: { type: 'output', source: 'step2_timelock_schedule' },
                    operation: '0',
                    safeTxGas: '0',
                    baseGas: '0',
                    gasPrice: '0',
                    gasToken: '0x0000000000000000000000000000000000000000',
                    refundReceiver: { type: 'output', source: 'step3a_multisig_selection' },
                    signatures: '0x'
                },
                outputMappings: {
                    executionTxHash: 'transaction_hash',
                    multiSigAddress: 'multisig_address',
                    functionName: 'function_name'
                },
                estimatedTime: 180, // 3 minutes
                dependencies: ['step3a_multisig_selection']
            }
        ]
    },
    {
        id: 'timelock_to_multisig',
        name: 'Timelock Execute → MultiSig',
        description: 'Execute a scheduled timelock operation through MultiSig',
        category: 'governance',
        difficulty: 'intermediate',
        estimatedTime: 10, // 10 minutes
        tags: ['timelock', 'execute', 'multisig', 'governance'],
        version: '1.0.0',
        prerequisites: [
            'Completed "Memer Function → Timelock → MultiSig" workflow',
            'Timelock delay period has passed',
            'MultiSig wallet access with required signatures'
        ],
        securityNotes: [
            'Verify the scheduled operation is still valid',
            'Confirm timelock delay has fully elapsed',
            'Double-check execution parameters match the original schedule'
        ],
        steps: [
            {
                id: 'step0_schedule_selection',
                title: 'Select Scheduled Operation',
                description: 'Choose from available scheduled timelock operations',
                type: 'schedule_selector',
                inputMappings: {
                    // Will load from workflow completion service
                },
                outputMappings: {
                    target: 'target_contract',
                    value: 'eth_value',
                    data: 'function_data',
                    predecessor: 'predecessor_hash',
                    salt: 'operation_salt',
                    delay: 'timelock_delay',
                    workflowName: 'source_workflow_name',
                    transactionHash: 'source_transaction_hash',
                    completionId: 'completion_record_id'
                },
                estimatedTime: 120 // 2 minutes
            },
            {
                id: 'step1_timelock_execute_builder',
                title: 'Build Timelock Execute',
                description: 'Build the timelock.execute() function call',
                type: 'function_builder',
                inputMappings: {
                    target: { type: 'output', source: 'step0_schedule_selection' },
                    value: { type: 'output', source: 'step0_schedule_selection' },
                    data: { type: 'output', source: 'step0_schedule_selection' },
                    predecessor: { type: 'output', source: 'step0_schedule_selection' },
                    salt: { type: 'output', source: 'step0_schedule_selection' }
                },
                outputMappings: {
                    executeFunctionData: 'encoded_execute_data',
                    timelockController: 'timelock_address',
                    originalScheduleData: 'schedule_parameters'
                },
                estimatedTime: 180, // 3 minutes
                dependencies: ['step0_schedule_selection']
            },
            {
                id: 'step2a_timelock_multisig_selection',
                title: 'Select MultiSig & Function',
                description: 'Choose the MultiSig wallet and execution function',
                type: 'function_builder',
                inputMappings: {
                    // User will select MultiSig contract and function
                },
                outputMappings: {
                    multiSigAddress: 'selected_multisig_address',
                    functionName: 'selected_function_name',
                    contractName: 'selected_contract_name'
                },
                estimatedTime: 120, // 2 minutes
                dependencies: ['step1_timelock_execute_builder']
            },
            {
                id: 'step2b_timelock_multisig_build',
                title: 'Build MultiSig Transaction',
                description: 'Build the MultiSig transaction to execute the timelock operation',
                type: 'function_builder',
                inputMappings: {
                    to: { type: 'output', source: 'step1_timelock_execute_builder' }, // timelock controller
                    value: '0',
                    data: { type: 'output', source: 'step1_timelock_execute_builder' }, // execute function data
                    operation: '0',
                    safeTxGas: '0',
                    baseGas: '0',
                    gasPrice: '0',
                    gasToken: '0x0000000000000000000000000000000000000000',
                    refundReceiver: { type: 'output', source: 'step2a_timelock_multisig_selection' },
                    signatures: '0x'
                },
                outputMappings: {
                    executionTxHash: 'transaction_hash',
                    multiSigAddress: 'multisig_address',
                    functionName: 'function_name',
                    finalExecutionData: 'final_transaction_data'
                },
                estimatedTime: 180, // 3 minutes
                dependencies: ['step2a_timelock_multisig_selection']
            }
        ]
    },
    {
        id: 'generic_timelock_function',
        name: 'Generic Timelock Function → MultiSig',
        description: 'Execute any timelock function directly through MultiSig (no scheduling required)',
        category: 'governance',
        difficulty: 'beginner',
        estimatedTime: 8, // 8 minutes
        tags: ['timelock', 'direct', 'multisig', 'governance', 'addApprovedTarget'],
        version: '1.0.0',
        prerequisites: [
            'Wallet connected with sufficient ETH for gas fees',
            'Access to MultiSig wallet with required signatures',
            'Timelock controller deployed and configured'
        ],
        securityNotes: [
            'Verify function parameters before execution',
            'Timelock self-operations execute immediately (no delay)',
            'Confirm MultiSig signers are available for execution'
        ],
        steps: [
            {
                id: 'step1_timelock_function_builder',
                title: 'Build Timelock Function',
                description: 'Select and configure any timelock function (addApprovedTarget, updateDelay, etc.)',
                type: 'function_builder',
                inputMappings: {
                    // User will select timelockController contract and function manually
                    // Contract will be filtered to show only timelockController
                },
                outputMappings: {
                    functionData: 'encoded_function_data',
                    timelockController: 'timelock_controller_address',
                    functionName: 'selected_function_name'
                },
                estimatedTime: 240 // 4 minutes
            },
            {
                id: 'step2a_multisig_selection',
                title: 'Select MultiSig & Function',
                description: 'Choose the MultiSig wallet and execution function (execTransaction or executeTransactionWithChecks)',
                type: 'function_builder',
                inputMappings: {
                    // User will select MultiSig contract and function
                },
                outputMappings: {
                    multiSigAddress: 'selected_multisig_address',
                    functionName: 'selected_function_name',
                    contractName: 'selected_contract_name'
                },
                estimatedTime: 120, // 2 minutes
                dependencies: ['step1_timelock_function_builder']
            },
            {
                id: 'step2b_multisig_execute',
                title: 'Build & Execute MultiSig Transaction',
                description: 'Build and execute the MultiSig transaction to call the timelock function',
                type: 'function_builder',
                inputMappings: {
                    to: { type: 'output', source: 'step1_timelock_function_builder' }, // timelock controller
                    value: '0',
                    data: { type: 'output', source: 'step1_timelock_function_builder' }, // timelock function data
                    operation: '0',
                    safeTxGas: '0',
                    baseGas: '0',
                    gasPrice: '0',
                    gasToken: '0x0000000000000000000000000000000000000000',
                    refundReceiver: { type: 'output', source: 'step2a_multisig_selection' },
                    signatures: '0x'
                },
                outputMappings: {
                    executionTxHash: 'transaction_hash',
                    multiSigAddress: 'multisig_address',
                    functionName: 'function_name',
                    finalExecutionData: 'final_transaction_data'
                },
                estimatedTime: 240, // 4 minutes
                dependencies: ['step2a_multisig_selection']
            }
        ]
    }
];

// Helper function to get template by ID
export const getWorkflowTemplate = (id: string): WorkflowTemplate | undefined => {
    return WORKFLOW_TEMPLATES.find(template => template.id === id);
};

// Helper function to get templates by category
export const getWorkflowTemplatesByCategory = (category: string): WorkflowTemplate[] => {
    return WORKFLOW_TEMPLATES.filter(template => template.category === category);
};

// Helper function to search templates
export const searchWorkflowTemplates = (query: string): WorkflowTemplate[] => {
    const lowercaseQuery = query.toLowerCase();
    return WORKFLOW_TEMPLATES.filter(template =>
        template.name.toLowerCase().includes(lowercaseQuery) ||
        template.description.toLowerCase().includes(lowercaseQuery) ||
        template.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
    );
};

// Alias for backward compatibility
export const searchWorkflows = searchWorkflowTemplates;

// Helper function to get workflows by category (for backward compatibility)
export const getWorkflowsByCategory = (category: string): WorkflowTemplate[] => {
    return getWorkflowTemplatesByCategory(category);
};

// Helper function to get workflows by difficulty
export const getWorkflowsByDifficulty = (difficulty: string): WorkflowTemplate[] => {
    return WORKFLOW_TEMPLATES.filter(template => template.difficulty === difficulty);
};
